import {
  Box,
  Button as ChakraButton,
  ButtonProps as ChakraButtonProps,
  CloseButton,
  IconButton as ChakraIconButton,
  IconButtonProps as ChakraIconButtonProps,
  PopoverProps,
  useStyleConfig
} from '@chakra-ui/react'
import { css } from '@emotion/react'
import classNames from 'classnames'
import { getFocusStyle } from 'front-end/src/components/chakraThemeExtension'
import React, { useState } from 'react'
import { brandColors } from 'shared/basicPalette'
import { getRgbFromHex, isColorDark } from 'shared/colorUtils'
import { useLooopTheme } from '../../../../public-api/hooks/useLooopTheme'
import Tooltip from '../../tooltip/Tooltip'

export interface IButtonProps {
  children: any
  to?: string
  className?: string
  hint?: string
  name?: string
  style?: Record<string, unknown>
  isLoading?: boolean
  iconOnly?: boolean
  leftIcon?: React.ReactElement
  disabled?: boolean
  active?: boolean
  spacingDisabled?: boolean
  accessibilityTitle?: string
  type?: 'submit' | 'reset' | 'button'
  progressPercents?: number | null
  dataTest?: string
  onClick?: (ev: React.MouseEvent<HTMLButtonElement>) => void
  onClickAsync?: (ev: React.MouseEvent<HTMLButtonElement>) => Promise<any> // same as onClick, but it will disable the button for the duration of the async function
}

export type ExtendedChakraButtonProps = IButtonProps & ChakraButtonProps

export const buttonEffects = (amount: number, darker = true) => {
  const hoverRatio = 0.5
  const activeRatio = 1
  const color = darker ? '0,0,0' : '255,255,255'
  return {
    _hover: {
      boxShadow: `inset 0 0 48px 48px rgba(${color},${amount * hoverRatio}%)`
    },
    _active: {
      boxShadow: `inset 0 0 48px 48px rgba(${color},${amount * activeRatio}%)`,
      transform: 'translateY(1px)'
    }
  }
}
const mainStyle = css`
  flex-shrink: 0;

  &.--iconOnly {
    display: inline-flex;
    padding: 9px 8px;
    flex: 0 0 auto;
    .Icon {
      margin: 0;
      min-height: 20px;
      display: inline-flex;
      align-items: center;
    }
  }
`

const ButtonBase = React.forwardRef<
  HTMLButtonElement,
  ExtendedChakraButtonProps
>(({ progressPercents, ...props }, ref) => {
  let buttonContent: JSX.Element
  const [loading, setLoading] = useState(false)

  if (typeof progressPercents === 'number') {
    buttonContent = (
      <>
        <Box
          as="span"
          css={css`
            width: ${progressPercents}%;
            opacity: 1;
            top: 0;
            left: 0;
            height: 100%;
            z-index: 1;
            transition: width 0.2s, opacity 0.2s;
            background-color: rgba(0, 0, 0, 0.2);
            position: absolute;
          `}
        />
        <>{props.children}</>
      </>
    )
  } else {
    buttonContent = <>{props.children}</>
  }

  const onClickHandler = props.onClickAsync
    ? async (ev) => {
        setLoading(true)
        try {
          await (
            props.onClickAsync as (
              ev: React.MouseEvent<HTMLButtonElement>
            ) => Promise<any>
          )(ev)
        } catch (err) {
          throw err
        } finally {
          setLoading(false)
        }
      }
    : props.onClick

  const {
    className,
    spacingDisabled,
    iconOnly,
    accessibilityTitle,
    onClickAsync,
    dataTest = 'Button',
    ...restOfProps
  } = props
  const cssClasses = classNames('Button', className, {
    '--spacingDisabled': spacingDisabled,
    '--iconOnly': iconOnly
  })

  const chakraButton = (
    <ChakraButton
      tabIndex={0}
      data-test={dataTest}
      role="button"
      className={cssClasses}
      css={mainStyle}
      h={props.h || props.height || 'auto'}
      pointerEvents={progressPercents ? 'none' : 'auto'}
      disabled={
        props.disabled || props.isDisabled || props.isLoading || loading
      }
      onClick={(e) => {
        onClickHandler && onClickHandler(e)
      }}
      ref={ref}
      position="relative"
      {...restOfProps}
    >
      {buttonContent}
      {accessibilityTitle && (
        <span style={{ display: 'none' }}>{accessibilityTitle}</span>
      )}
    </ChakraButton>
  )

  if (props.hint) {
    return <Tooltip text={props.hint}>{chakraButton}</Tooltip>
  } else return chakraButton
})

export const ButtonPrimary = (props: ExtendedChakraButtonProps) => {
  const { theme } = useLooopTheme()

  return (
    <ButtonBase
      backgroundColor={theme.colors.buttonPrimary.default}
      color={
        isColorDark(theme.colors.buttonPrimary.default)
          ? brandColors.white
          : brandColors.grey.dark
      }
      {...buttonEffects(35, !isColorDark(theme.colors.buttonPrimary.default))}
      {...props}
    />
  )
}

export const ButtonLight = React.forwardRef<
  HTMLButtonElement,
  ExtendedChakraButtonProps
>((props, ref) => {
  return (
    <ButtonBase
      backgroundColor={brandColors.grey[100]}
      color={brandColors.grey[800]}
      {...buttonEffects(15)}
      {...props}
      ref={ref}
    />
  )
})

export const ButtonDark = (props: ExtendedChakraButtonProps) => {
  return (
    <ButtonBase
      backgroundColor={brandColors.grey.mid}
      color={brandColors.grey.lightest}
      {...buttonEffects(15)}
      {...props}
    />
  )
}

export const ButtonClean = (props: ExtendedChakraButtonProps) => {
  return (
    <ButtonBase
      backgroundColor="transparent"
      color={brandColors.grey.mid}
      _hover={{
        color: brandColors.grey[700]
      }}
      _active={{
        transform: 'translateY(1px)'
      }}
      {...props}
    />
  )
}

export const ButtonDanger = (props: ExtendedChakraButtonProps) => {
  return (
    <ButtonBase
      backgroundColor={brandColors.red.mid}
      color={brandColors.white}
      _hover={{
        bg: brandColors.red[500]
      }}
      _active={{
        bg: brandColors.red[500],
        transform: 'translateY(1px)'
      }}
      {...props}
    />
  )
}

export const ButtonInfo = (props: ExtendedChakraButtonProps) => {
  return (
    <ButtonBase
      backgroundColor={brandColors.blue.mid}
      color={brandColors.white}
      _hover={{
        bg: brandColors.blue[500]
      }}
      _active={{
        bg: brandColors.blue[500],
        transform: 'translateY(1px)'
      }}
      {...props}
    />
  )
}

export const ButtonOutline = (props: ExtendedChakraButtonProps) => {
  const size = props.size || 'md'
  const styles = useStyleConfig('Button', { size })
  const { theme } = useLooopTheme()

  return (
    <ButtonBase
      backgroundColor="transparent"
      color={brandColors.grey[700]}
      borderWidth="1px"
      borderColor={brandColors.grey[300]}
      px={`${parseInt(styles.px as string) - 1}px`}
      py={`${parseInt(styles.py as string) - 1}px`}
      {...buttonEffects(10)}
      _active={{
        bg: 'transparent',
        color: theme.colors.buttonPrimary.default,
        borderColor: theme.colors.buttonPrimary.default
      }}
      {...props}
    />
  )
}

export const ButtonOutlinePrimary = (props: ExtendedChakraButtonProps) => {
  const size = props.size || 'md'
  const { theme } = useLooopTheme()
  const styles = useStyleConfig('Button', { size })
  const { r, g, b } = getRgbFromHex(theme.colors.buttonPrimary.default)
  return (
    <ButtonBase
      backgroundColor="transparent"
      color={theme.colors.buttonPrimary.default}
      borderWidth="1px"
      borderColor={theme.colors.buttonPrimary.default}
      px={`${parseInt(styles.px as string) - 1}px`}
      py={`${parseInt(styles.py as string) - 1}px`}
      _hover={{
        bg: `rgba(${r}, ${g}, ${b}, 0.15)`,
        boxShadow: 'inset 0 0 48px 48px rgba(0,0,0,5%)',
        color: `${theme.colors.buttonPrimary.default}`
      }}
      _active={{
        bg: `rgba(${r}, ${g}, ${b}, 0.3)`,
        boxShadow: 'inset 0 0 48px 48px rgba(0,0,0,10%)',
        color: `${theme.colors.buttonPrimary.default}`,
        transform: 'translateY(1px)'
      }}
      {...props}
    />
  )
}

export const ButtonShadow = (props: ExtendedChakraButtonProps) => {
  return (
    <ButtonBase
      bg="rgba(255, 255, 255, 0.33)"
      color={brandColors.grey.mid}
      boxShadow="1px 1px 0 0 rgb(0 0 0 / 3%), 2px 2px 15px -2px rgb(8 16 24 / 10%)"
      transition="all 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)"
      _hover={{
        boxShadow:
          '-1px -1px 4px 0 rgb(0 0 0 / 2%), 0 6px 10px rgb(8 16 24 / 10%)',
        transform: 'scale3d(1.005, 1.005, 1) translateY(-2px)'
      }}
      _active={{
        boxShadow: '0 4px 6px rgba(8, 16, 24, 0.1)',
        transform: 'scale3d(1.005, 1.005, 1) translateY(0px)'
      }}
      {...props}
    />
  )
}

export const ButtonClose = (props: ChakraButtonProps) => {
  return (
    <CloseButton
      backgroundColor={brandColors.white}
      border="none"
      color={brandColors.grey.mid}
      {...props}
    />
  )
}

export type ButtonIconPropType = ChakraIconButtonProps & {
  hint?: string | JSX.Element
  circular?: boolean
  dataTest?: string
  hintProps?: PopoverProps
}

export const ButtonIcon = (props: ButtonIconPropType) => {
  const {
    hint,
    circular,
    dataTest = 'IconButton',
    hintProps,
    variant,
    ...rest
  } = props
  const { theme } = useLooopTheme()
  const button =
    variant === 'outline' ? (
      // TODO: Use only this variant in future code
      // After replacing all old instances we will move this into the ChakraThemeExtension.tsx
      <ChakraIconButton
        bg="none"
        borderWidth="1px"
        color={brandColors.grey[700]}
        borderColor={brandColors.grey[300]}
        data-test={dataTest}
        borderRadius={circular ? '50%' : undefined}
        _focusVisible={getFocusStyle(theme.colors.buttonPrimary.mid)}
        {...buttonEffects(15)}
        {...rest}
      />
    ) : (
      <ChakraIconButton
        bg="none"
        border="none"
        data-test={dataTest}
        borderRadius={circular ? '50%' : undefined}
        _focusVisible={getFocusStyle(theme.colors.buttonPrimary.mid)}
        {...buttonEffects(15)}
        {...rest}
      />
    )

  return hint ? (
    <Tooltip delay={500} text={hint} tabIndex={-1} popoverProps={hintProps}>
      {button}
    </Tooltip>
  ) : (
    button
  )
}
